import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Send, Loader2 } from 'lucide-react';
import { QuoteAPI } from '../lib/api';

const QuoteRequestDialog = ({ product, children }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '' });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Missing fields', description: 'Name and email are required.' });
      return;
    }
    setSubmitting(true);
    try {
      await QuoteAPI.create({ product_id: product.id, ...form });
      toast({ title: 'Quote request sent', description: 'Our specialist will contact you within 24 hours.' });
      setForm({ name: '', email: '', company: '', phone: '', message: '' });
      setOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send the request. Try again.';
      toast({ title: 'Error', description: typeof msg === 'string' ? msg : 'Something went wrong.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black text-[#050F2E] tracking-tight">Request a quote</DialogTitle>
          <DialogDescription className="text-slate-600">
            We&apos;ll send pricing and availability for <span className="font-bold text-[#0A2A63]">{product?.name}</span> within 24 hours.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="grid gap-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="q-name" className="text-xs uppercase tracking-widest text-slate-500 font-bold">Name *</Label>
              <Input id="q-name" name="name" value={form.name} onChange={onChange} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="q-email" className="text-xs uppercase tracking-widest text-slate-500 font-bold">Email *</Label>
              <Input id="q-email" type="email" name="email" value={form.email} onChange={onChange} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="q-company" className="text-xs uppercase tracking-widest text-slate-500 font-bold">Company</Label>
              <Input id="q-company" name="company" value={form.company} onChange={onChange} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="q-phone" className="text-xs uppercase tracking-widest text-slate-500 font-bold">Phone</Label>
              <Input id="q-phone" name="phone" value={form.phone} onChange={onChange} className="mt-1.5 h-11 rounded-xl" />
            </div>
          </div>
          <div>
            <Label htmlFor="q-message" className="text-xs uppercase tracking-widest text-slate-500 font-bold">Message</Label>
            <Textarea id="q-message" name="message" rows={4} value={form.message} onChange={onChange} className="mt-1.5 rounded-xl resize-none" placeholder="Quantities, timeline, integration needs..." />
          </div>
          <Button type="submit" disabled={submitting} className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-11 font-bold btn-transition w-full">
            {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>) : (<><Send className="h-4 w-4 mr-2" /> Send request</>)}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteRequestDialog;
